import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * @typedef {('asc' | 'desc' | null | undefined)} SortDirection
 */

/**
 * @typedef {object} SortIndicatorProps
 * @property {string} label - The text label for the column (e.g., "Name", "Date").
 * @property {SortDirection} direction - Current sort direction.
 * @property {(label: string, newDirection: SortDirection) => void} onClick - Callback when clicked.
 * @property {object} [sx] - Optional: Styles for the main Box component.
 * @property {object} [labelSx] - Optional: Styles for the label Typography component.
 * @property {object} [iconSx] - Optional: Styles for the SortIcon component.
 */

/**
 * A reusable component for creating sortable column headers in a table.
 * It displays a label and a sort icon indicating the current sort direction.
 * Clicking the component cycles through ascending, descending, and no sort states.
 * @param {SortIndicatorProps} props - The props for the SortIndicator component.
 */
const SortIndicator = ({
  label, // The text label for the column (e.g., "Name", "Date")
  direction, // Current sort direction
  onClick, // Callback when clicked
  // Optional: Add other props for styling if needed
  sx,
  labelSx,
  iconSx,
}) => {
  // Determine which icon to show
  const SortIcon = direction === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;

  // Function to handle the click and determine the next sort direction
  const handleClick = (label, clickedDirection) => {
    onClick(label, clickedDirection);
  };

  return (
    <Box
      sx={{
        //width: "11%", // As in your example
        maxWidth: "8rem",
        minWidth: "8rem",
        textWrap: "wrap",
        // mx: "5px",
        textAlign: "center",
        display: 'flex', // Use flexbox for alignment
        alignItems: 'center', // Vertically center text and icon
        justifyContent: 'center', // Horizontally center content
        //gap: 0.5, // Small gap between text and icon
        cursor: 'pointer', // Indicate it's clickable
        '&:hover': {
          opacity: 0.8, // Slight hover effect
        },
        ...sx, // Allow overriding styles from parent
      }}
    // onClick={handleClick} // Make the entire Box clickable
    >
      <Typography
        sx={{
          fontWeight: "bold",
          color: "#F28627",
          fontSize: ".8em",
          ...labelSx, // Allow overriding label styles
        }}
      >
        {label}
      </Typography>
      {/* Only show the icon if a direction is set */}
      {direction === null ? (
        <>
          <ArrowUpwardIcon
            sx={{
              fontSize: '0.8em',
              color: '#F28627',
              ...iconSx,
            }}
            onClick={() => handleClick(label, 'asc')}
          />
          <ArrowDownwardIcon
            sx={{
              fontSize: '0.8em',
              color: '#F28627',
              ...iconSx,
            }}
            onClick={() => handleClick(label, 'desc')}
          />
        </>
      ) : (
        <SortIcon
          sx={{
            fontSize: '0.8em',
            color: '#F28627',
            ...iconSx,
          }}
          onClick={() => handleClick(label, null)}
        />
      )}
    </Box>
  );
};

export default SortIndicator;